/**
 * Error Boundary Component
 *
 * Catches unhandled React errors and displays a graceful error page with:
 * - Correlation ID for support reference (links to App Insights logs)
 * - Link to contact support
 * - Application Insights logging
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { analyticsService } from '../../../services/analytics.service';
import { isChunkLoadError } from '../../../utils/lazyWithRetry';
import { logger } from '../../../utils/logger';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  correlationId: string | null;
  error: Error | null;
  isChunkError: boolean;
}

/**
 * Error Boundary Component
 *
 * Wraps application components to catch any unhandled errors and display
 * a user-friendly error page instead of a blank screen.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      correlationId: null,
      error: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Get correlation ID from Application Insights for log tracing
    const correlationId = analyticsService.getOperationId();
    const chunkError = isChunkLoadError(error);

    // If it's a chunk load error (stale deployment), auto-reload after a short delay
    // to fetch the new index.html with updated chunk references
    if (chunkError) {
      logger.warn('[ErrorBoundary] Chunk load error detected — will auto-reload', {
        event: 'frontend.chunk_load_error',
        error: error.message,
      });
    }

    return {
      hasError: true,
      correlationId,
      error,
      isChunkError: chunkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { correlationId, isChunkError } = this.state;

    logger.error('[ErrorBoundary] Uncaught error', error, {
      event: isChunkError ? 'frontend.chunk_load_error' : 'frontend.react_error_boundary',
      correlationId,
      componentStack: errorInfo.componentStack,
    });

    // Report to backend for ADO ticket creation + admin email notification
    // Fire-and-forget: never await or let failure here mask the original error
    if (!isChunkError) {
      const apiBase = (import.meta as any).env?.VITE_API_URL ?? '';
      fetch(`${apiBase}/api/errors/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-ID': correlationId ?? analyticsService.getOperationId(),
        },
        body: JSON.stringify({
          errorMessage: error.message,
          errorStack: error.stack,
          componentStack: errorInfo.componentStack ?? undefined,
          correlationId: correlationId ?? analyticsService.getOperationId(),
          url: window.location.href,
          userAgent: navigator.userAgent,
          pageTitle: document.title,
        }),
      }).catch((reportErr) => {
        logger.warn('[ErrorBoundary] Failed to send error report', {
          event: 'frontend.error_report_failed',
          error: reportErr instanceof Error ? reportErr.message : String(reportErr),
        });
      });
    }

    // Auto-reload for chunk errors after a brief delay so the user sees the message
    if (isChunkError) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }

  handleCopyCorrelationId = async (): Promise<void> => {
    const { correlationId } = this.state;
    if (correlationId) {
      try {
        await navigator.clipboard.writeText(correlationId);
        // Could add a toast notification here
      } catch (err) {
        logger.error('Failed to copy correlation ID', err);
      }
    }
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, correlationId, isChunkError } = this.state;
    const { children } = this.props;

    if (hasError && isChunkError) {
      // Chunk load error — show a brief "updating" message before auto-reload
      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.iconWrapper}>
              <svg
                className={styles.icon}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
            <h1 className={styles.title}>Updating Application</h1>
            <p className={styles.description}>
              A new version of the application is available. The page will reload automatically in a
              moment.
            </p>
            <div className={styles.actions}>
              <button onClick={this.handleReload} className={styles.reloadButton} type="button">
                Reload Now
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className={styles.container}>
          <div className={styles.content}>
            {/* Error Icon */}
            <div className={styles.iconWrapper}>
              <svg
                className={styles.icon}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>

            {/* Title */}
            <h1 className={styles.title}>Something went wrong</h1>

            {/* Description */}
            <p className={styles.description}>
              We apologize for the inconvenience. The application ran into an unexpected issue. Our
              team has been notified and is looking into it.
            </p>

            {/* Correlation ID */}
            <div className={styles.errorIdContainer}>
              <span className={styles.errorIdLabel}>Reference ID:</span>
              <code className={styles.errorId}>{correlationId}</code>
              <button
                onClick={this.handleCopyCorrelationId}
                className={styles.copyButton}
                title="Copy reference ID"
                type="button"
              >
                <svg
                  className={styles.copyIcon}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>

            {/* Support Info */}
            <p className={styles.supportText}>
              Need immediate assistance? Contact us at{' '}
              <a
                href={`mailto:support@mindalyx.com?subject=Error Report: ${correlationId}`}
                className={styles.supportEmail}
              >
                support@mindalyx.com
              </a>
            </p>

            {/* Actions */}
            <div className={styles.actions}>
              <button onClick={this.handleReload} className={styles.reloadButton} type="button">
                Reload Application
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
